import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarClock,
  CheckCircle2,
  Database,
  HardDrive,
  Layers,
  Minus,
  Plus,
  Receipt,
  Sparkles,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Pagination } from '@/components/ui/Table';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, ErrorState, PageLoader } from '@/components/ui/States';
import { CheckoutDialog } from '@/components/billing/CheckoutDialog';
import { formatBytes, formatDate, formatIdr, formatNumber, formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * Lama periode ditulis dalam hari oleh server. Diterjemahkan ke kalimat yang
 * biasa dibaca orang, bukan "per 180 hari".
 */
const sebutPeriode = (hari) => {
  if (hari % 30 === 0 && hari >= 30) {
    const bulan = hari / 30;
    return bulan === 1 ? 'bulan' : `${bulan} bulan`;
  }
  if (hari % 7 === 0 && hari >= 7) {
    const minggu = hari / 7;
    return minggu === 1 ? 'minggu' : `${minggu} minggu`;
  }
  return hari === 1 ? 'hari' : `${hari} hari`;
};

const WARNA_STATUS = {
  paid: 'success',
  pending: 'warning',
  expired: 'neutral',
  failed: 'danger',
  refunded: 'neutral',
};

const NAMA_STATUS = {
  paid: 'Lunas',
  pending: 'Menunggu pembayaran',
  expired: 'Kedaluwarsa',
  failed: 'Gagal',
  refunded: 'Dikembalikan',
};

/**
 * Add-on dikelompokkan menurut jenis kuotanya.
 *
 * Digabung dalam satu daftar, pembeli harus membaca satu per satu untuk tahu
 * mana yang menambah baris dan mana yang menambah penyimpanan. Padahal ia
 * biasanya datang karena satu kuota tertentu yang habis, bukan karena ingin
 * melihat-lihat.
 */
const KELOMPOK_ADDON = [
  {
    jenis: 'records',
    judul: 'Tambahan Baris Data',
    keterangan: 'untuk yang datanya lebih banyak dari kuota paket',
    ikon: Database,
  },
  {
    jenis: 'storage',
    judul: 'Tambahan Penyimpanan',
    keterangan: 'untuk berkas hasil yang kolomnya banyak atau isinya panjang',
    ikon: HardDrive,
  },
  {
    jenis: 'jobs',
    judul: 'Tambahan Job',
    keterangan: 'untuk yang perlu menyimpan lebih banyak pekerjaan',
    ikon: Layers,
  },
  {
    jenis: 'schedules',
    judul: 'Tambahan Jadwal',
    keterangan: 'untuk yang perlu lebih banyak jadwal berjalan bersamaan',
    ikon: CalendarClock,
  },
];

/** Satuan tiap jenis add-on, supaya angkanya ditulis dengan benar. */
const sebutBesaran = (type, amount) => {
  if (type === 'storage') return formatBytes(amount);
  if (type === 'records') return `${formatNumber(amount)} baris`;
  if (type === 'jobs') return `${formatNumber(amount)} job`;
  return `${formatNumber(amount)} jadwal`;
};

/**
 * Layar Paket & Tagihan.
 *
 * Satu halaman yang menjawab empat pertanyaan sekaligus: paket saya apa,
 * sisa kuota berapa, bagaimana menambahnya, dan apa saja yang sudah saya
 * bayar. Memecahnya jadi empat halaman terpisah membuat orang harus
 * berpindah-pindah untuk menjawab satu pertanyaan sederhana.
 */
export const BillingPage = () => {
  const [keranjang, setKeranjang] = useState(null);

  // Paket dan kode diskon yang dibawa dari halaman harga, lewat alamat.
  //
  // Ada supaya satu klik pada tombol diskon di pengumuman bisa berujung di
  // halaman pembayaran tanpa terputus: pengunjung memilih paket sebelum
  // masuk, lalu dilempar ke halaman masuk, dan sesudah masuk ia harus
  // mendarat tepat di tempat ia berhenti. Tanpa ini, ia mendarat di dashboard
  // dan harus mengulang seluruh pilihannya dari awal.
  const [pencarian, setPencarian] = useSearchParams();
  const [kodeDibawa, setKodeDibawa] = useState('');

  useEffect(() => {
    const planId = pencarian.get('plan');
    const kode = pencarian.get('kode');
    if (!planId && !kode) return;

    if (planId) {
      setKodeDibawa(kode ?? '');
      setKeranjang({ planId });
    }

    // Parameternya dibersihkan dari alamat supaya menyegarkan halaman tidak
    // membuka dialog pembayaran lagi, dan supaya alamat yang disalin orang
    // tidak diam-diam membawa kode diskon milik orang lain.
    setPencarian({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [jumlahAddOn, setJumlahAddOn] = useState({});

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['usage'],
    queryFn: () => api.get('/dashboard/usage'),
  });

  // Harga diambil dari server, bukan ditulis mati di sini. Admin bisa
  // mengubahnya lewat Manajemen Web > Harga Paket, dan daftar yang ditulis
  // mati akan diam-diam menampilkan harga lama sesudah perubahan itu.
  const { data: dataPaket } = useQuery({
    queryKey: ['public-plans'],
    queryFn: () => api.get('/public/plans'),
    staleTime: 5 * 60_000,
  });

  const { data: dataAddOn } = useQuery({
    queryKey: ['addon-catalog'],
    queryFn: () => api.get('/billing/addons'),
  });

  const { data: dataAktif } = useQuery({
    queryKey: ['addon-active'],
    queryFn: () => api.get('/billing/addons/aktif'),
  });

  /**
   * Riwayat tagihan dibaca sepuluh baris sekaligus.
   *
   * Akun yang sudah lama dipakai membuat halaman ini harus digulir jauh ke
   * bawah hanya untuk melewati riwayatnya, sementara yang dicari orang di sana
   * biasanya justru satu transaksi tertentu.
   */
  const [halamanTagihan, setHalamanTagihan] = useState(1);
  const { data: dataTagihan } = useQuery({
    queryKey: ['transactions', halamanTagihan],
    queryFn: () => api.get('/billing/transactions', { params: { page: halamanTagihan, limit: 10 } }),
    placeholderData: (sebelumnya) => sebelumnya,
  });
  const metaTagihan = dataTagihan?.meta;

  if (isLoading) return <PageLoader label="Memuat data pemakaian..." />;
  if (isError) {
    return (
      <Card>
        <ErrorState message={error?.message} onRetry={refetch} />
      </Card>
    );
  }

  const usage = data.data;
  const paket = (dataPaket?.data ?? []).filter((p) => !p.hidden);
  const katalog = dataAddOn?.data;
  const addOnAktif = dataAktif?.data ?? [];
  const tagihan = dataTagihan?.data ?? [];

  const ubahJumlah = (id, delta, maks) =>
    setJumlahAddOn((s) => ({ ...s, [id]: Math.max(1, Math.min(maks, (s[id] ?? 1) + delta)) }));

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader title="Paket Saat Ini" action={<Badge tone="brand">{usage.plan.name}</Badge>} />
        <CardBody className="space-y-5">
          <UsageBar
            label="Baris data"
            used={usage.records.used}
            limit={usage.records.limit}
            percent={usage.records.percent}
            tambahan={usage.addOns?.records}
            format={formatNumber}
          />
          <UsageBar
            label="Job tersimpan"
            used={usage.jobs.used}
            limit={usage.jobs.limit}
            percent={usage.jobs.percent}
            tambahan={usage.addOns?.jobs}
            format={formatNumber}
          />
          <UsageBar
            label="Penyimpanan"
            used={usage.storage.used}
            limit={usage.storage.limit}
            percent={usage.storage.percent}
            tambahan={usage.addOns?.storage}
            format={formatBytes}
          />

          <dl className="grid gap-x-8 gap-y-2 border-t border-[var(--border-subtle)] pt-4 sm:grid-cols-2">
            {[
              ['Kuota berulang pada', formatDate(usage.periodEnd)],
              ['Retensi data', `${usage.retentionDays} hari`],
              ['Crawl bersamaan', `${usage.concurrentRuns.used} / ${usage.concurrentRuns.limit}`],
              ['Batas jadwal aktif', formatNumber(usage.features?.maxSchedules ?? 0)],
              ['Akses API', usage.features.apiAccess ? 'Aktif' : 'Belum tersedia'],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between gap-4 text-sm">
                <dt className="text-[var(--text-muted)]">{label}</dt>
                <dd className="font-semibold text-[var(--text-primary)]">{value}</dd>
              </div>
            ))}
          </dl>
        </CardBody>
      </Card>

      {addOnAktif.length > 0 && (
        <Card>
          <CardHeader
            title="Add-on Aktif"
            description="Kuota tambahan yang sedang berlaku, di atas paket Anda."
          />
          <CardBody className="space-y-2">
            {addOnAktif.map((a) => (
              <div
                key={a.id}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-[var(--border-subtle)] px-4 py-2.5"
              >
                <CheckCircle2 className="size-4 shrink-0 text-success-600" aria-hidden="true" />
                <span className="min-w-0 flex-1 text-sm font-medium text-[var(--text-primary)]">
                  {a.name}
                </span>
                <span className="text-sm text-[var(--text-secondary)]">
                  {sebutBesaran(a.type, a.amount * a.quantity)}
                </span>
                <span className="text-xs text-[var(--text-muted)]">
                  sampai {formatDate(a.expiresAt)}
                </span>
              </div>
            ))}
          </CardBody>
        </Card>
      )}

      <div>
        <h2 className="text-lg font-bold text-[var(--text-primary)]">Pilihan Paket</h2>
        <p className="mt-1 text-sm text-[var(--text-muted)]">
          Membeli paket yang sama saat masih berlaku akan menambah masa aktifnya.
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {paket.map((plan, urutan) => {
            const isCurrent = plan.id === usage.plan.id;
            const gratis = plan.priceIdr === 0;

            // Peringkat diambil dari urutan daftar, yang memang disusun dari
            // termurah ke termahal oleh server. Turun paket dimatikan selama
            // paket yang lebih tinggi masih berlaku: pindah ke bawah akan
            // menghanguskan sisa masa aktif yang sudah dibayar, dan itu
            // sepenuhnya merugikan pelanggan.
            const peringkatSekarang = paket.findIndex((p) => p.id === usage.plan.id);
            const lebihRendah = peringkatSekarang > -1 && urutan < peringkatSekarang;
            const masihBerlaku = usage.periodEnd && new Date(usage.periodEnd) > new Date();
            const dikunci = gratis || (lebihRendah && masihBerlaku);

            return (
              <Card
                key={plan.id}
                className={cn('flex flex-col p-5', isCurrent && 'border-brand-950 dark:border-accent-500')}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-[var(--text-primary)]">{plan.name}</p>
                  {isCurrent && (
                    <Badge tone="brand" size="sm">
                      Aktif
                    </Badge>
                  )}
                </div>

                <p className="mt-3 text-2xl font-extrabold text-[var(--text-primary)]">
                  {gratis ? 'Gratis' : formatIdr(plan.priceIdr)}
                </p>
                <p className="mt-0.5 text-xs text-[var(--text-muted)]">
                  per {sebutPeriode(plan.periodDays)}
                </p>
                {plan.tagline && (
                  <p className="mt-2 text-xs text-[var(--text-muted)]">{plan.tagline}</p>
                )}

                {/* `flex-1` membuat daftar fitur mengisi sisa ruang kartu,
                    sehingga tombol di bawahnya berbaris rata di semua kartu
                    berapa pun panjang daftarnya. Sebelumnya jaraknya
                    ditentukan panjang daftar masing-masing, jadi tiap kartu
                    berbeda. */}
                <ul className="mt-4 flex-1 space-y-1.5 border-t border-[var(--border-subtle)] pt-4">
                  {[
                    `${formatNumber(plan.maxRecords)} baris`,
                    `${formatNumber(plan.maxJobs)} job`,
                    `${formatBytes(plan.storageBytes)} penyimpanan`,
                    plan.scheduleAllowed
                      ? `${formatNumber(plan.maxSchedules)} jadwal otomatis`
                      : 'Tanpa penjadwalan',
                  ].map((baris) => (
                    <li key={baris} className="flex items-start gap-2">
                      <CheckCircle2
                        className="mt-0.5 size-4 shrink-0 text-success-600"
                        aria-hidden="true"
                      />
                      <span className="text-sm text-[var(--text-secondary)]">{baris}</span>
                    </li>
                  ))}
                </ul>

                {/* Keterangannya di ATAS tombol, bukan di bawahnya.
                    Tombol adalah hal terakhir yang dibaca mata sebelum
                    diklik, jadi ia harus selalu berada di dasar kartu.
                    Keterangan yang menyusul sesudahnya menggeser tombol ke
                    ketinggian yang berbeda-beda antar kartu, dan barisan
                    tombol yang tidak rata membuat seluruh daftar terlihat
                    seperti tersusun keliru. */}
                <div className="mt-4">
                  {lebihRendah && masihBerlaku && !gratis && (
                    <p className="mb-2 text-center text-xs text-[var(--text-muted)]">
                      Tersedia setelah {formatDate(usage.periodEnd)}
                    </p>
                  )}
                  <Button
                    variant={isCurrent ? 'secondary' : 'primary'}
                    size="sm"
                    className="w-full"
                    disabled={dikunci}
                    icon={dikunci ? undefined : Sparkles}
                    onClick={() => setKeranjang({ planId: plan.id })}
                  >
                    {gratis
                      ? 'Paket Gratis'
                      : isCurrent
                        ? 'Perpanjang'
                        : lebihRendah && masihBerlaku
                          ? 'Lebih rendah'
                          : 'Pilih Paket'}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <div>
        <h2 className="text-lg font-bold text-[var(--text-primary)]">Tambah Kuota</h2>
        <p className="mt-1 text-sm text-[var(--text-muted)]">
          Menambah kuota tanpa mengganti paket. Add-on berlaku sampai masa aktif paket berakhir.
        </p>

        {!katalog ? null : !katalog.tersedia ? (
          <Card className="mt-4">
            <EmptyState compact icon={Sparkles} title="Belum tersedia" description={katalog.alasan} />
          </Card>
        ) : katalog.items.length === 0 ? (
          <Card className="mt-4">
            <EmptyState
              compact
              icon={Sparkles}
              title="Belum ada add-on"
              description="Belum ada kuota tambahan yang dijual untuk paket Anda."
            />
          </Card>
        ) : (
          <div className="mt-4 space-y-8">
            {KELOMPOK_ADDON.map((kel) => {
              const anggota = katalog.items.filter((a) => a.type === kel.jenis);
              if (anggota.length === 0) return null;

              return (
                <section key={kel.jenis}>
                  <div className="mb-3 flex items-center gap-2">
                    <kel.ikon className="size-4 text-[var(--text-muted)]" aria-hidden="true" />
                    <h3 className="text-sm font-bold text-[var(--text-primary)]">{kel.judul}</h3>
                    <span className="text-xs text-[var(--text-muted)]">{kel.keterangan}</span>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {anggota.map((a) => {
              const jumlah = jumlahAddOn[a.id] ?? 1;
              return (
                <Card key={a.id} className="flex flex-col p-5">
                  <p className="text-sm font-bold text-[var(--text-primary)]">{a.name}</p>
                  <p className="mt-1 text-xs text-[var(--text-muted)]">
                    {sebutBesaran(a.type, a.amount)} per satuan
                  </p>
                  {a.description && (
                    <p className="mt-2 text-sm text-[var(--text-secondary)]">{a.description}</p>
                  )}

                  <p className="mt-3 text-xl font-extrabold text-[var(--text-primary)]">
                    {formatIdr(a.hargaSekarang * jumlah)}
                  </p>
                  {a.hargaSekarang < a.hargaPenuh && (
                    <p className="text-xs text-[var(--text-muted)]">
                      Dihitung dari sisa {katalog.sisaHari} hari periode Anda
                    </p>
                  )}

                  <div className="mt-4 flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={Minus}
                      iconOnly
                      aria-label="Kurangi jumlah"
                      disabled={jumlah <= 1}
                      onClick={() => ubahJumlah(a.id, -1, a.maxQuantity)}
                    />
                    <span className="min-w-8 text-center text-sm font-bold text-[var(--text-primary)]">
                      {jumlah}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={Plus}
                      iconOnly
                      aria-label="Tambah jumlah"
                      disabled={jumlah >= a.maxQuantity}
                      onClick={() => ubahJumlah(a.id, 1, a.maxQuantity)}
                    />
                    <Button
                      size="sm"
                      className="ml-auto"
                      onClick={() => setKeranjang({ addOns: [{ addOnId: a.id, quantity: jumlah }] })}
                    >
                      Beli
                    </Button>
                  </div>
                </Card>
              );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      <Card>
        <CardHeader
          title="Riwayat Tagihan"
          description={metaTagihan?.total ? `${metaTagihan.total} pembelian, beserta status pembayarannya.` : 'Pembelian Anda beserta status pembayarannya.'}
        />
        {tagihan.length === 0 ? (
          <EmptyState
            compact
            icon={Receipt}
            title="Belum ada transaksi"
            description="Tagihan akan muncul di sini setelah Anda membeli paket atau add-on."
          />
        ) : (
          <CardBody className="space-y-2">
            {tagihan.map((t) => (
              <div
                key={t.id}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-[var(--border-subtle)] px-4 py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-[var(--text-primary)]">
                    {t.items.map((i) => i.name).join(', ')}
                  </p>
                  <p className="truncate font-mono text-xs text-[var(--text-muted)]">
                    {t.orderId} · {formatRelative(t.createdAt)}
                  </p>
                </div>
                {t.discount?.amountIdr > 0 && (
                  <Badge tone="success" size="sm">
                    {t.discount.code}
                  </Badge>
                )}
                <span className="tabular text-sm font-semibold text-[var(--text-primary)]">
                  {formatIdr(t.amountIdr)}
                </span>
                <Badge tone={WARNA_STATUS[t.status] ?? 'neutral'} size="sm">
                  {NAMA_STATUS[t.status] ?? t.status}
                </Badge>
                {t.status === 'pending' && t.paymentUrl?.startsWith('http') && (
                  <a
                    href={t.paymentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm font-semibold underline underline-offset-2"
                  >
                    Bayar
                  </a>
                )}
              </div>
            ))}
            {metaTagihan?.totalPages > 1 && (
              <Pagination
                page={metaTagihan.page}
                totalPages={metaTagihan.totalPages}
                total={metaTagihan.total}
                limit={metaTagihan.limit}
                onPageChange={setHalamanTagihan}
                className="pt-2"
              />
            )}
          </CardBody>
        )}
      </Card>

      {keranjang && (
        <CheckoutDialog
          keranjang={keranjang}
          kodeAwal={kodeDibawa}
          onClose={() => {
            setKeranjang(null);
            setKodeDibawa('');
          }}
        />
      )}
    </div>
  );
};

const UsageBar = ({ label, used, limit, percent, format, tambahan = 0 }) => (
  <div>
    <div className="flex items-baseline justify-between text-sm">
      <span className="font-semibold text-[var(--text-primary)]">{label}</span>
      <span className="tabular text-[var(--text-muted)]">
        {format(used)} dari {format(limit)}
        {tambahan > 0 && (
          <span className="ml-1 text-success-600">termasuk add-on {format(tambahan)}</span>
        )}
      </span>
    </div>

    <div
      className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--surface-sunken)]"
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Pemakaian ${label}`}
    >
      <div
        className={cn(
          'h-full rounded-full transition-[width] duration-500',
          // Warna berubah lebih awal dari batas, supaya peringatan tiba
          // sebelum pengguna benar-benar terhalang.
          percent >= 90 ? 'bg-danger-500' : percent >= 70 ? 'bg-warning-500' : 'bg-accent-600',
        )}
        style={{ width: `${Math.min(100, percent)}%` }}
      />
    </div>
  </div>
);
